import NoData from '@root/components/common/NoData';
import MuiTable, { DEFAULT_PAGE_SIZE, DEFAULT_ROWS_PER_PAGE_OPTIONS } from '@root/components/table/table';
// import { VulnerabilityCounts } from './VulnerabilityCounts';
import useEnhancedQuery from '@root/query/useEnhancedQuery';
import useDebouncedValue from '@root/hooks/useDebouncedValue';
import agent from '@root/agent';
import useSortData from '@root/hooks/useSortData';
import HeaderBarNew from '@root/components/HeaderBarNew';
import {
  useCallback,
  useEffect,
  // useEffect,
  useMemo,
  useState,
} from 'react';
import { CustomCursorFooter } from '@root/components/table/CustomCursorFooter';
// import _ from 'lodash';
import { useEnvironments, useSelectedEnvironments } from '@root/hooks/environments';
import { sbomColumns } from './sbomColumns';
// import TableSkeleton from '@root/components/table/skeleton';
import SideOverlay from '../Sideoverlay';
// import FilterComponent from '@root/components/FilterNew';
// import { capitalizeFirstLetter } from '@root/utilities/string';
import { useCloudProvider } from '@root/components/v2/content/CloudProviderBar/CloudProviderContext';
import PaginatedFilter from '@root/components/FilterNew/PaginatedFilter';
import { toast } from 'sonner';
import { exportPipeline } from '../exportPipeline';
const EXPORTED_COLS = [
  'account_id',
  'cluster',
  'name',
  'type',
  'version',
  'related_images',
  'related_workloads',
  'related_namespaces',
  'vulnerability_counts',
];

const EXPORTED_HEADERNAME = [
  'Account ID',
  'Cluster',
  'Component',
  'Type',
  'Fix Version',
  'Images',
  'Workloads',
  'Namespaces',
  'Vulnerabilities',
];
const SBOM = ({ isEnabled }) => {
  const [searchString, setSearchString] = useState('');
  const [currentPage, setCurrentPage] = useState(0); // Start from 0 for cursor pagination
  const [cursors, setCursors] = useState(['']); // Stack of cursors, empty string for first page
  const [hasNextPage, setHasNextPage] = useState(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const debouncedSearchString = useDebouncedValue(searchString, 750);
  const { sortData, handleSortModelChange } = useSortData();
  const { selectedEnvironments } = useSelectedEnvironments();
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    component: [],
    images: [],
    workloads: [],
    clusters: [],
  });
  // const [isFetchingPropertyKeyValue, setIsFetchingPropertyKeyValue] = useState(false);
  // const [propertyKeyValueData, setPropertyKeyValueData] = useState(null);
  const { cloudProvider } = useCloudProvider();
  const allEnvironments = useEnvironments();
  const environments = useMemo(() => {
    if (selectedEnvironments.length && cloudProvider !== 'all') {
      return selectedEnvironments.filter(environment => environment.cloudProvider === cloudProvider);
    } else if (cloudProvider === 'all') {
      return selectedEnvironments;
    }
    return allEnvironments;
  }, [selectedEnvironments, cloudProvider, allEnvironments]);
  // const [isDefaultValue, setIsDefaultValue] = useState(false);
  const { data: sbomData, isFetching: isFetchingSbomData } = useEnhancedQuery(
    [
      'get_sbom_table_data',
      debouncedSearchString,
      selectedEnvironments.map(item => item.id),
      currentPage, // Added to trigger refetch when page changes
      cursors[currentPage],
      pageSize,
      sortData,
      isEnabled,
      selectedFilters,
    ],
    () =>
      agent.Kubernetes.get_sbom_table_data({
        environments: selectedEnvironments.map(item => item.id),
        sort: sortData?.map(item => {
          return {
            field: item.key,
            order: item.value,
          };
        }),
        components: selectedFilters?.component,
        images: selectedFilters?.images,
        workloads: selectedFilters?.workloads,
        clusters: selectedFilters?.clusters || [],
        risk_spotlight: isEnabled,
        search_query: debouncedSearchString ? debouncedSearchString : '',
        cursor: currentPage === 0 ? null : cursors[currentPage], // First page has no cursor
        page_number: currentPage + 1,
        rows_per_page: pageSize,
        type: selectedFilters?.type || [],
        export: false,
      }),
  );

  useEffect(() => {
    if (isFetchingSbomData) return;
    if (sbomData?.pagination) {
      const { has_next, next_cursor } = sbomData.pagination;
      setHasNextPage(has_next);

      // Store next cursor if we have one and don't already have it
      if (has_next && next_cursor) {
        setCursors(prev => {
          const nextIndex = currentPage + 1;
          // Only update if we don't have this cursor yet or if it's different
          if (!prev[nextIndex] || prev[nextIndex] !== next_cursor) {
            const newCursors = [...prev];
            newCursors[nextIndex] = next_cursor;
            return newCursors;
          }
          return prev;
        });
      }
    }
  }, [sbomData, currentPage, isFetchingSbomData]);

  // Reset pagination when filters/sort/search changes
  useEffect(() => {
    setCurrentPage(0);
    setCursors([null]); // First page has no cursor
  }, [debouncedSearchString, selectedFilters, sortData, selectedEnvironments, isEnabled]);

  const handleNextPage = useCallback(() => {
    if (hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  }, [hasNextPage]);

  const handlePrevPage = useCallback(() => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
    }
  }, [currentPage]);

  const handlePageSizeChange = useCallback(_pageSize => {
    setPageSize(_pageSize);
    setCurrentPage(0);
    setCursors(['']);
    setHasNextPage(false);
  }, []);

  const fetchKubernetesSBOMFilterPropertyValues = useCallback(
    async paginatedFilter => {
      const response = await agent.Kubernetes.get_sbom_filter_data({
        environments: environments.map(e => e.id),
        clusters: [],
        search_key: paginatedFilter ? paginatedFilter?.search_key : '',
        filter_particular_key: paginatedFilter
          ? paginatedFilter?.filter_particular_key?.toLowerCase().replace(/\s+/g, '_')
          : '',
        previous_filter: [],

        page_no: paginatedFilter ? paginatedFilter?.page_no : 1,
        row_per_page: paginatedFilter ? paginatedFilter?.row_per_page : 20,
      });
      return response;
    },
    [environments],
  );

  const propertyValues = useMemo(() => {
    // if (isFetchingPropertyKeyValue || !propertyKeyValueData?.data) {
    return {
      Clusters: {
        data: [],
        num_records: 0,
        has_more: false,
      },
      Type: {
        data: [],
        num_records: 0,
        has_more: false,
      },
      Component: {
        data: [],
        num_records: 0,
        has_more: false,
      },
      Images: {
        data: [],
        num_records: 0,
        has_more: false,
      },
      Workloads: {
        data: [],
        num_records: 0,
        has_more: false,
      },
      // Pods: {
      //   data: [],
      //   num_records: 0,
      //   has_more: false,
      // },
      // Containers: {
      //   data: [],
      //   num_records: 0,
      //   has_more: false,
      // },
    };
  }, []);

  const _tableData = useMemo(() => {
    if (isFetchingSbomData || !sbomData?.data) return [];
    return sbomData?.data?.map((v, i) => ({ ...v, id: i })) ?? [];
  }, [isFetchingSbomData, sbomData?.data]);

  const handleRowClick = useCallback(r => {
    setSelectedRow(r.row);
    setShowDetailsPanel(true);
  }, []);

  const handleCloseDetailsPanel = useCallback(() => {
    setShowDetailsPanel(false);
    setSelectedRow(null);
  }, []);

  const handleExportData = useCallback(async () => {
    toast.success('Exporting data...');

    try {
      const response = await agent.Kubernetes.get_sbom_table_data({
        // scope: selectedEnvironmentTags,
        environments: selectedEnvironments.map(item => item.id),
        sort: sortData?.map(item => {
          return {
            field: item.key,
            order: item.value,
          };
        }),
        components: selectedFilters?.component,
        images: selectedFilters?.images,
        workloads: selectedFilters?.workloads,
        pods: selectedFilters?.pods,
        containers: selectedFilters?.containers,
        clusters: selectedFilters?.clusters || [],
        risk_spotlight: isEnabled,
        search_query: debouncedSearchString ? debouncedSearchString : '',
        page_number: currentPage,
        rows_per_page: pageSize,
        type: selectedFilters?.type || [],
        export: true,
      });

      if (response?.data) {
        const data = response.data?.map(item => {
          const _item = {};
          for (const key of EXPORTED_COLS) {
            if (item[key]) {
              _item[key] = item[key];
            } else {
              _item[key] = '-';
            }
          }
          return {
            ..._item,
            related_images: JSON.stringify(_item['related_images']),
            related_workloads: JSON.stringify(_item['related_workloads']),
            related_namespaces: JSON.stringify(_item['related_namespaces']),
            vulnerability_counts: JSON.stringify(_item['vulnerability_counts']),
          };
        });

        exportPipeline(data, EXPORTED_COLS, EXPORTED_HEADERNAME, 'Kubernetes SBOM');
      }

      toast.success('Data exported successfully.');
    } catch (err) {
      console.log(err);
      toast.error(`Failed to export data. ${err.message}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedEnvironments,
    sortData,
    selectedFilters?.clusters,
    selectedFilters?.severity,
    selectedFilters?.fixable,
    selectedFilters.cvss_attack_vector,
    selectedFilters.epss_score,
    selectedFilters?.exploitable,
    selectedFilters?.cve_id,
    debouncedSearchString,
    currentPage,
    pageSize,
    isEnabled,
  ]);

  return (
    <div style={{ margin: '15px' }}>
      <HeaderBarNew
        // marginTop={'5px'}
        marginBottom={'13px'}
        title={''}
        searchBar
        searchBarValue={searchString}
        setOpenFilter={setOpenFilter}
        openFilter={openFilter}
        exportButton={true}
        disableExportMenu
        onExportButtonClick={handleExportData}
        setSearchTerm={setSearchString}
        searchBarOnChange={e => setSearchString(e.target.value)}
      />{' '}
      <PaginatedFilter
        openFilter={openFilter}
        option={propertyValues}
        setSelectedFilters={setSelectedFilters}
        selectedFilters={selectedFilters}
        fetchFilterPropertyValues={fetchKubernetesSBOMFilterPropertyValues}
        // isKubernetes={true}
        defaultValue={{}}
      />
      <MuiTable
        columns={sbomColumns}
        paginationMode={'server'}
        rows={_tableData}
        LoaderState={isFetchingSbomData}
        components={{
          NoRowsOverlay: () => <NoData message={'No SBOM present!'} />,
          Footer: () => (
            <CustomCursorFooter
              currentPage={currentPage}
              hasNextPage={hasNextPage}
              onNextPage={handleNextPage}
              onPrevPage={handlePrevPage}
              loading={isFetchingSbomData}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
              rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
            />
          ),
        }}
        sortingMode="server"
        handleSortModelChange={handleSortModelChange}
        total_size={-1}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        noRowsMessage={'No SBOM Present!'}
        rowHeight={49.9}
        onRowClick={handleRowClick}
        isDynamicHeight={true}
        cursorPagination={true}
        currentPage={currentPage}
        hasNextPage={hasNextPage}
        onNextPage={handleNextPage}
        onPrevPage={handlePrevPage}
      />
      {showDetailsPanel && (
        <SideOverlay
          key={showDetailsPanel.toString()}
          onClose={handleCloseDetailsPanel}
          selectedRow={selectedRow}
          type={'sbom'}
        />
      )}
    </div>
  );
};
export default SBOM;
